import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import apiClient from '../apiClient';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('access_token');
    if (token) {
      // Set default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Fetch user data
      apiClient.get('/auth/me')
        .then(response => {
          setUser(response.data);
        })
        .catch((error) => {
          // If token is invalid or expired, clear it and redirect to login
          console.log('🔐 Token validation failed, redirecting to login');
          localStorage.removeItem('access_token');
          localStorage.removeItem('ezocmUser');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
          
          // Force redirect to sign-in modal
          window.location.href = '/signin';
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const response = await apiClient.post('/auth/login', { email, password });
      const { access_token, user } = response.data;
      
      // Validate user role
      if (!user.role) {
        console.error('User role is missing:', user);
        return {
          success: false,
          error: 'Invalid user data received from server'
        };
      }
  
      // Save token and user data
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('ezocmUser', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Set user in context
      setUser(user);
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error.response?.data || error);
      
      // Handle cold start scenarios with specific messaging
      if (error.isRetrying) {
        return {
          success: false,
          error: 'System is initializing, please wait...',
          isInitializing: true,
          retryAttempt: error.retryAttempt,
          maxRetries: error.maxRetries
        };
      }
      
      // Check for cold start indicators
      const isColdStartError = 
        error.code === 'ECONNABORTED' ||
        error.response?.status === 503 ||
        error.response?.status === 502 ||
        error.response?.status === 504 ||
        error.message?.includes('timeout') ||
        error.message?.includes('Network Error');
      
      if (isColdStartError) {
        return {
          success: false,
          error: 'System is starting up. This may take a moment on first access.',
          isColdStart: true
        };
      }
      
      // Check if backend returned a structured error object
      if (error.response?.data && typeof error.response.data === 'object' && error.response.data.error) {
        return {
          success: false,
          error: error.response.data  // Pass the entire error object
        };
      }
      
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || 'Login failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('ezocmUser');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    // Redirect to login page
    window.location.href = '/signin';
  };

  const value = {
    user,
    loading,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 
